import { Injectable } from '@angular/core';
import { FormRule, FormRuleFactory, RuleAction } from 'src/app/shared/forms';
import { TokenClaimsService } from 'src/app/shared/services';
import { SubmissionConstants } from '../models/constants/submissions-constants';
import { FormGroup } from '@angular/forms';
import { NeemV3CustomRules } from './custom-rules/neemV3Rules';
import { SbraV3CustomRules } from './custom-rules/sbraV3Rules';
import { CustomerInfoRules } from './custom-rules/customerRules';
import { ManufactureDateRules } from './custom-rules/manufactureDateRules';
import { SbraV2CustomRules } from './custom-rules/sbraV2Rules';
import { HeatPumpDocumentRules } from './custom-rules/heatPumpDocumentRules';

@Injectable({ providedIn: 'root' })
export class CustomFormRulesService {
  constructor(
    private tokenService: TokenClaimsService,
    private ruleFact: FormRuleFactory,
    private submissionsConst: SubmissionConstants,
  ) {}

  getCustomRules(formStructureId: string, formValues: FormGroup): FormRule[] {
    switch (formStructureId.toString()) {
      case '2':
        return [
          ...new SbraV2CustomRules(
            this.ruleFact,
            this.submissionsConst,
          ).getCustomRules(formValues),
          ...this.getCustomVersionTwoAdminRules(formValues),
        ];
      case '1':
        return [
          ...new SbraV3CustomRules(
            this.ruleFact,
            this.submissionsConst,
          ).getCustomRules(formValues),
          ...this.getCustomVersionThreeRules(formValues),
          ...this.getCustomVersionThreeAdminRules(formValues),
        ];
      case '4':
        return [
          ...this.getCustomNeemVersionTwoRules(formValues),
          ...this.getCustomNeemVersionTwoAdminRules(formValues),
        ];
      case '3':
        return [
          ...new NeemV3CustomRules(
            this.ruleFact,
            this.submissionsConst,
          ).getCustomRules(formValues),
          ...this.getCustomNeemVersionThreeAdminRules(formValues),
        ];
      default:
        return [];
    }
  }
  private getCustomVersionTwoAdminRules(subValues: FormGroup): FormRule[] {
    return [];
  }

  private getCustomVersionThreeRules(subValues: FormGroup): FormRule[] {
    const zeroEnergyElectric = this.mapBooleanValues(
      subValues.get('zeroEnergyElectric')?.value,
    );
    const zeroEnergyGas = this.mapBooleanValues(
      subValues.get('zeroEnergyGas')?.value,
    );

    const isAlternateConstructionSerial = this.isAlternateConstructionSerial(
      subValues.get('homeSerialNumber')?.value,
    );
    const homeCenterType = subValues.get('homeCenterType')?.value;
    const externalDuctLocation = subValues.get('externalDuctLocation').value;
    const hasExternalDucts = subValues.get('hasExternalDucts').value;
    const status = subValues.get('status').value;
    return [
      ...new CustomerInfoRules(
        this.ruleFact,
        this.submissionsConst,
      ).getCustomRules(subValues),
      this.ruleFact.createRule(`'${homeCenterType}' === '1'`, [
        {
          action: RuleAction.MAKE_VISIBLE,
          affectingType: 'control',
          applyTo: 'noInvoiceAvailable',
        },
      ]),
      ...new ManufactureDateRules(
        this.ruleFact,
        this.submissionsConst,
      ).getCustomRules(subValues),
      this.ruleFact.createRule(
        `(${zeroEnergyElectric} || ${zeroEnergyGas}) &&
        (${!isAlternateConstructionSerial})`,
        [
          {
            action: RuleAction.MAKE_DISABLED,
            affectingType: 'control',
            applyTo: 'waterHeating',
          },
          {
            action: RuleAction.SET_TOOLTIP,
            affectingType: 'control',
            applyTo: 'waterHeating',
            value: this.submissionsConst.waterHeatingEBuiltWarning,
          },
        ],
      ),
      this.ruleFact.createSingleExecutionRule(
        `(${zeroEnergyElectric} || ${zeroEnergyGas}) &&
        (${!isAlternateConstructionSerial})`,
        [
          {
            action: RuleAction.SET_VALUE,
            affectingType: 'control',
            applyTo: 'waterHeating',
            value: '',
          },
        ],
      ),
      this.ruleFact.createRule(
        `(${!zeroEnergyElectric} && ${!zeroEnergyGas}) &&
        (${!isAlternateConstructionSerial})`,
        [
          {
            action: RuleAction.MAKE_DISABLED,
            affectingType: 'control',
            applyTo: 'waterHeating',
          },
        ],
      ),
      this.ruleFact.createSingleExecutionRule(
        `(${!zeroEnergyElectric} && ${!zeroEnergyGas}) &&
        (${!isAlternateConstructionSerial})`,
        [
          {
            action: RuleAction.SET_VALUE,
            affectingType: 'control',
            applyTo: 'waterHeating',
            value: this.submissionsConst.waterHeatingOmitWarning,
          },
        ],
      ),
      this.ruleFact.createRule(
        `(${zeroEnergyElectric} || ${zeroEnergyGas}) &&
        (${isAlternateConstructionSerial})`,
        [
          {
            action: RuleAction.MAKE_DISABLED,
            affectingType: 'control',
            applyTo: 'waterHeating',
          },
          {
            action: RuleAction.HIDE_TOOLTIP,
            affectingType: 'control',
            applyTo: 'waterHeating',
          },
        ],
      ),
      this.ruleFact.createSingleExecutionRule(
        `(${zeroEnergyElectric} || ${zeroEnergyGas}) &&
        (${isAlternateConstructionSerial})`,
        [
          {
            action: RuleAction.SET_VALUE,
            affectingType: 'control',
            applyTo: 'waterHeating',
            value: this.submissionsConst.waterHeating30AmpWarning,
          },
        ],
      ),
      this.ruleFact.createSingleExecutionRule(
        `(${!zeroEnergyElectric} && ${!zeroEnergyGas}) &&
        (${isAlternateConstructionSerial})`,
        [
          {
            action: RuleAction.SET_VALUE,
            affectingType: 'control',
            applyTo: 'waterHeating',
            value: this.submissionsConst.waterHeatingOmitWarning,
          },
          {
            action: RuleAction.HIDE_TOOLTIP,
            affectingType: 'control',
            applyTo: 'waterHeating',
          },
        ],
      ),
      this.ruleFact.createRule(
        `'{primarySpaceHeatingCooling}' === 'Omit Furnace'`,
        [
          {
            action: RuleAction.MAKE_HIDDEN,
            affectingType: 'group',
            applyTo: 'Heat Pump',
          },
        ],
      ),
      this.ruleFact.createRule(
        `${!hasExternalDucts} && ${!externalDuctLocation}`,
        [
          {
            action: RuleAction.MAKE_INVALID,
            affectingType: 'control',
            applyTo: 'externalDuctLocation',
          },
        ],
      ),
      this.ruleFact.createRule(
        `${status === 'Ready To Submit'} && ${hasExternalDucts}`,
        [
          {
            action: RuleAction.SET_VALUE,
            affectingType: 'control',
            applyTo: 'externalDuctLocation',
            value: '',
          },
        ],
      ),
    ];
  }

  private getCustomVersionThreeAdminRules(subValues: FormGroup): FormRule[] {
    const energyEfficiencyType = subValues.get('energyEfficiencyType')?.value;
    const zeroEnergyElectric = this.mapBooleanValues(
      subValues.get('zeroEnergyElectric')?.value,
    );
    const zeroEnergyGas = this.mapBooleanValues(
      subValues.get('zeroEnergyGas')?.value,
    );
    const isAlternateConstructionSerial = this.isAlternateConstructionSerial(
      subValues.get('homeSerialNumber')?.value,
    );

    if (!this.tokenService.isEnergyCreditAdmin) {
      return [];
    }

    return [
      this.ruleFact.createRule(`${true}`, [
        {
          action: RuleAction.MAKE_ENABLED,
          affectingType: 'control',
          applyTo: 'isEBuilt',
        },
      ]),
      this.ruleFact.createRule(
        `'${energyEfficiencyType}' === 'none' || !'${energyEfficiencyType}'`,
        [
          {
            action: RuleAction.MAKE_HIDDEN,
            affectingType: 'control',
            applyTo: 'energyEfficiencyTypeBanner',
          },
        ],
      ),

      this.ruleFact.createRule(`(${zeroEnergyElectric} || ${zeroEnergyGas})`, [
        {
          action: RuleAction.MAKE_ENABLED,
          affectingType: 'control',
          applyTo: 'waterHeating',
        },
      ]),
      this.ruleFact.createSingleExecutionRule(
        `(${zeroEnergyElectric} || ${zeroEnergyGas}) &&
        (${isAlternateConstructionSerial})`,
        [
          {
            action: RuleAction.SET_VALUE,
            affectingType: 'control',
            applyTo: 'waterHeating',
            value: this.submissionsConst.waterHeating30AmpWarning,
          },
        ],
      ),
      this.ruleFact.createRule(
        `(${!zeroEnergyElectric} && ${!zeroEnergyGas})`,
        [
          {
            action: RuleAction.MAKE_ENABLED,
            affectingType: 'control',
            applyTo: 'waterHeating',
          },
        ],
      ),
      this.ruleFact.createSingleExecutionRule(
        `(${!zeroEnergyElectric} && ${!zeroEnergyGas})`,
        [
          {
            action: RuleAction.SET_VALUE,
            affectingType: 'control',
            applyTo: 'waterHeating',
            value: this.submissionsConst.waterHeatingOmitWarning,
          },
        ],
      ),
    ];
  }

  private getCustomNeemVersionTwoRules(subValues: FormGroup): FormRule[] {
    const designPackage = subValues.get('designPackage').value;
    return [
      ...new CustomerInfoRules(
        this.ruleFact,
        this.submissionsConst,
      ).getCustomRules(subValues),
      ...new ManufactureDateRules(
        this.ruleFact,
        this.submissionsConst,
      ).getCustomRules(subValues),
      ...new HeatPumpDocumentRules(
        this.ruleFact,
        this.submissionsConst,
      ).getV2CustomRules(subValues),
      this.ruleFact.createRule(`(${designPackage === null})`, [
        {
          action: RuleAction.MAKE_HIDDEN,
          affectingType: 'group',
          applyTo: 'Heat Pump Information',
        },
      ]),
      this.ruleFact.createRule(
        `'{status}' === 'Ready To Submit' && '{designPackage}' === null`,
        [
          {
            action: RuleAction.MAKE_INVALID,
            affectingType: 'control',
            applyTo: 'designPackage',
          },
          {
            action: RuleAction.SET_ERROR_MESSAGE,
            affectingType: 'control',
            applyTo: 'designPackage',
            value: 'this is required',
          },
        ],
      ),
      this.ruleFact.createRule(
        `'{status}' !== 'Ready To Submit' && '{designPackage}' === null`,
        [
          {
            action: RuleAction.CLEAR_ERROR_MESSAGE,
            affectingType: 'control',
            applyTo: 'designPackage',
          },
          {
            action: RuleAction.MAKE_NOT_REQUIRED,
            affectingType: 'control',
            applyTo: 'designPackage',
          },
        ],
      ),
    ];
  }
  private getCustomNeemVersionTwoAdminRules(subValues: FormGroup): FormRule[] {
    return [];
  }

  private getCustomNeemVersionThreeAdminRules(
    subValues: FormGroup,
  ): FormRule[] {
    if (!this.tokenService.isEnergyCreditAdmin) {
      return [];
    }
    const energyEfficiencyType = subValues.get('energyEfficiencyType')?.value;
    return [
      this.ruleFact.createRule(`${true}`, [
        {
          action: RuleAction.MAKE_ENABLED,
          affectingType: 'control',
          applyTo: 'isEBuilt',
        },
      ]),
      this.ruleFact.createRule(
        `'${energyEfficiencyType}' === 'none' || !'${energyEfficiencyType}'`,
        [
          {
            action: RuleAction.MAKE_HIDDEN,
            affectingType: 'control',
            applyTo: 'energyEfficiencyTypeBanner',
          },
        ],
      ),
    ];
  }

  private isAlternateConstructionSerial(serialNumber: string): boolean {
    return (serialNumber ?? '').indexOf('AC') !== -1;
  }

  private mapBooleanValues(val: any): boolean {
    return val === 'true' || val === true
      ? true
      : val === '' || val === null || val === 'false'
        ? false
        : false;
  }
}
